import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, message, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import Api from '../../Network';
import NameByLang from '../../Component/nameByLang';
import './SalesRepresentativeModal.scss';

const { Option } = Select;

interface SalesRepresentativeModalProps {
  visible: boolean;
  mode: 'create' | 'edit' | 'view';
  salesRepresentative: any;
  onClose: () => void;
  onSuccess?: () => void;
}

const SalesRepresentativeModal: React.FC<SalesRepresentativeModalProps> = ({
  visible,
  mode,
  salesRepresentative,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Fetch inventories for dropdown
  const { data: inventoriesData, isLoading: inventoriesLoading } = useQuery({
    queryKey: ['inventories'],
    queryFn: async () => {
      const response = await Api.get('api/admin/inventories');
      return response;
    },
    enabled: visible && (mode === 'create' || mode === 'edit'),
  });

  // Create sales representative mutation
  const createSalesRepMutation = useMutation({
    mutationFn: (data: any) => Api.post('api/admin/drivers', data),
    onSuccess: () => {
      message.success(
        t('salesRepresentatives.addSuccess', 'Sales representative added successfully')
      );
      form.resetFields();
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      if (error?.response?.status === 422) {
        // Handle validation errors
        const responseData = error.response.data;

        // Show the msg field if available (e.g., "The phone number has already been taken.")
        if (responseData?.msg) {
          message.error(responseData.msg);
        } else {
          // Fallback to validation errors
          const validationErrors = responseData?.errors;
          if (validationErrors) {
            // Set form field errors
            const fieldErrors: any = {};
            Object.keys(validationErrors).forEach((field) => {
              const errors = validationErrors[field];
              if (Array.isArray(errors) && errors.length > 0) {
                fieldErrors[field] = errors[0];
              }
            });

            // Set form errors
            form.setFields(
              Object.keys(fieldErrors).map((field) => ({
                name: field,
                errors: [fieldErrors[field]],
              }))
            );

            // Show first validation error as message
            const firstError = Object.values(validationErrors)[0];
            if (Array.isArray(firstError) && firstError.length > 0) {
              message.error(firstError[0]);
            } else {
              message.error(t('validation.error', 'Validation error occurred'));
            }
          } else {
            message.error(t('validation.error', 'Validation error occurred'));
          }
        }
      } else {
        const errorMessage =
          error?.response?.data?.message ||
          t('common.error', 'Error creating sales representative');
        message.error(errorMessage);
      }
    },
  });

  // Update sales representative mutation
  const updateSalesRepMutation = useMutation({
    mutationFn: (data: any) => Api.put(`api/admin/drivers/${salesRepresentative?.id}`, data),
    onSuccess: () => {
      message.success(
        t('salesRepresentatives.updateSuccess', 'Sales representative updated successfully')
      );
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      if (error?.response?.status === 422) {
        // Handle validation errors
        const responseData = error.response.data;

        // Show the msg field if available (e.g., "The phone number has already been taken.")
        if (responseData?.msg) {
          message.error(responseData.msg);
        } else {
          // Fallback to validation errors
          const validationErrors = responseData?.errors;
          if (validationErrors) {
            // Set form field errors
            const fieldErrors: any = {};
            Object.keys(validationErrors).forEach((field) => {
              const errors = validationErrors[field];
              if (Array.isArray(errors) && errors.length > 0) {
                fieldErrors[field] = errors[0];
              }
            });

            // Set form errors
            form.setFields(
              Object.keys(fieldErrors).map((field) => ({
                name: field,
                errors: [fieldErrors[field]],
              }))
            );

            // Show first validation error as message
            const firstError = Object.values(validationErrors)[0];
            if (Array.isArray(firstError) && firstError.length > 0) {
              message.error(firstError[0]);
            } else {
              message.error(t('validation.error', 'Validation error occurred'));
            }
          } else {
            message.error(t('validation.error', 'Validation error occurred'));
          }
        }
      } else {
        const errorMessage =
          error?.response?.data?.message ||
          t('common.error', 'Error updating sales representative');
        message.error(errorMessage);
      }
    },
  });

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      if (mode === 'create') {
        await createSalesRepMutation.mutateAsync(values);
      } else if (mode === 'edit') {
        // For edit mode, only send the inventory_id
        const editData = {
          inventory_id: values.inventory_id,
        };
        await updateSalesRepMutation.mutateAsync(editData);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  // Clear form errors when user starts typing
  const handleFieldChange = () => {
    form.setFields(
      form.getFieldsError().map((field) => ({
        name: field.name,
        errors: [],
      }))
    );
  };

  // Set form values when editing or viewing
  useEffect(() => {
    if (visible && salesRepresentative && (mode === 'edit' || mode === 'view')) {
      // Get inventory_id from the inventories array
      let inventoryId = salesRepresentative.inventory_id;
      if (
        Array.isArray(salesRepresentative.inventories) &&
        salesRepresentative.inventories.length > 0
      ) {
        inventoryId = salesRepresentative.inventories[0].id;
      }

      form.setFieldsValue({
        name: salesRepresentative.name,
        phone_number: salesRepresentative.phone_number,
        password: mode === 'view' ? '••••••••' : '', // Don't show password in view mode
        inventory_id: inventoryId?.toString(),
      });
    } else if (visible && mode === 'create') {
      form.resetFields();
    }
  }, [visible, salesRepresentative, mode, form]);

  const getModalTitle = () => {
    switch (mode) {
      case 'create':
        return t('salesRepresentatives.addNew', 'Add New Sales Representative');
      case 'edit':
        return (
          t('common.edit', 'Edit') +
          ' ' +
          t('salesRepresentatives.form.inventory', 'Inventory') +
          ' - ' +
          salesRepresentative?.name
        );
      case 'view':
        return (
          t('common.view', 'View') + ' ' + t('salesRepresentatives.title', 'Sales Representative')
        );
      default:
        return '';
    }
  };

  const isViewMode = mode === 'view';

  return (
    <Modal
      title={getModalTitle()}
      open={visible}
      onCancel={handleCancel}
      footer={
        isViewMode
          ? [
              <Button key="close" onClick={handleCancel}>
                {t('common.close', 'Close')}
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={handleCancel}>
                {t('common.cancel', 'Cancel')}
              </Button>,
              <Button key="submit" type="primary" loading={loading} onClick={() => form.submit()}>
                {t('common.save', 'Save')}
              </Button>,
            ]
      }
      width={600}
      destroyOnClose
    >
      <Spin spinning={inventoriesLoading}>
        <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={isViewMode}>
          {/* Show all fields for create mode */}
          {mode === 'create' && (
            <>
              <Form.Item
                name="name"
                label={t('salesRepresentatives.form.name', 'Name')}
                rules={[
                  {
                    required: true,
                    message: t('validation.required', 'This field is required'),
                  },
                ]}
              >
                <Input
                  placeholder={t(
                    'salesRepresentatives.form.namePlaceholder',
                    'Enter sales representative name'
                  )}
                  disabled={isViewMode}
                  onChange={handleFieldChange}
                />
              </Form.Item>

              <Form.Item
                name="phone_number"
                label={t('salesRepresentatives.form.phoneNumber', 'Phone Number')}
                rules={[
                  {
                    required: true,
                    message: t('validation.required', 'This field is required'),
                  },
                  {
                    pattern: /^[0-9+\-\s()]+$/,
                    message: t('validation.invalidPhone', 'Please enter a valid phone number'),
                  },
                ]}
              >
                <Input
                  placeholder={t(
                    'salesRepresentatives.form.phoneNumberPlaceholder',
                    'Enter phone number'
                  )}
                  disabled={isViewMode}
                  onChange={handleFieldChange}
                />
              </Form.Item>

              <Form.Item
                name="password"
                label={t('salesRepresentatives.form.password', 'Password')}
                rules={[
                  {
                    required: true,
                    message: t('validation.required', 'This field is required'),
                  },
                  {
                    min: 6,
                    message: t('validation.minLength', 'Minimum 6 characters required'),
                  },
                ]}
              >
                <Input.Password
                  placeholder={t('salesRepresentatives.form.passwordPlaceholder', 'Enter password')}
                  disabled={isViewMode}
                  onChange={handleFieldChange}
                />
              </Form.Item>
            </>
          )}

          {/* Show only inventory field for edit mode */}
          {mode === 'edit' && (
            <Form.Item
              name="inventory_id"
              label={t('salesRepresentatives.form.inventory', 'Inventory')}
              rules={[
                {
                  required: true,
                  message: t('validation.required', 'This field is required'),
                },
              ]}
            >
              <Select
                placeholder={t('salesRepresentatives.form.selectInventory', 'Select inventory')}
                loading={inventoriesLoading}
                onChange={handleFieldChange}
              >
                {inventoriesData?.data?.map((inventory: any) => (
                  <Option key={inventory.id} value={inventory.id.toString()}>
                    <NameByLang
                      name_en={inventory.name}
                      name_ar={inventory.ar_name}
                      fallback={inventory.name || 'Unknown'}
                    />
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* Show all fields for view mode */}
          {mode === 'view' && (
            <>
              <Form.Item name="name" label={t('salesRepresentatives.form.name', 'Name')}>
                <Input disabled={true} />
              </Form.Item>

              <Form.Item
                name="phone_number"
                label={t('salesRepresentatives.form.phoneNumber', 'Phone Number')}
              >
                <Input disabled={true} />
              </Form.Item>

              <Form.Item
                name="password"
                label={t('salesRepresentatives.form.password', 'Password')}
              >
                <Input.Password disabled={true} />
              </Form.Item>

              <Form.Item
                name="inventory_id"
                label={t('salesRepresentatives.form.inventory', 'Inventory')}
              >
                <Select disabled={true}>
                  {inventoriesData?.data?.map((inventory: any) => (
                    <Option key={inventory.id} value={inventory.id.toString()}>
                      <NameByLang
                        name_en={inventory.name}
                        name_ar={inventory.ar_name}
                        fallback={inventory.name || 'Unknown'}
                      />
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}

          {/* Show inventory field for create mode */}
          {mode === 'create' && (
            <Form.Item
              name="inventory_id"
              label={t('salesRepresentatives.form.inventory', 'Inventory')}
              rules={[
                {
                  required: true,
                  message: t('validation.required', 'This field is required'),
                },
              ]}
            >
              <Select
                placeholder={t('salesRepresentatives.form.selectInventory', 'Select inventory')}
                loading={inventoriesLoading}
                onChange={handleFieldChange}
              >
                {inventoriesData?.data?.map((inventory: any) => (
                  <Option key={inventory.id} value={inventory.id.toString()}>
                    <NameByLang
                      name_en={inventory.name}
                      name_ar={inventory.ar_name}
                      fallback={inventory.name || 'Unknown'}
                    />
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default SalesRepresentativeModal;
